import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { ERouterName } from '/@/types/index'
import CreatePlan from '/@/components/task/CreatePlan.vue'
import WaylinePanel from '/@/pages/page-web/projects/wayline.vue'
import DockPanel from '/@/pages/page-web/projects/dock.vue'
import { hasWebSession } from '/@/utils/auth-session'

function redirectAuthenticatedWebUser () {
  return hasWebSession() ? { name: ERouterName.DASHBOARD } : true
}

function requireWebSession () {
  return hasWebSession() ? true : { name: ERouterName.PROJECT }
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    redirect: '/' + ERouterName.DASHBOARD
  },
  // 首页
  {
    path: '/' + ERouterName.PROJECT,
    name: ERouterName.PROJECT,
    component: () => import('/@/pages/page-web/index.vue'),
    beforeEnter: redirectAuthenticatedWebUser
  },
  // members, devices
  {
    path: '/' + ERouterName.HOME,
    name: ERouterName.HOME,
    component: () => import('/@/pages/page-web/home.vue'),
    children: [
      {
        path: '/' + ERouterName.DASHBOARD,
        name: ERouterName.DASHBOARD,
        component: () => import('/@/pages/page-web/projects/dashboard.vue'),
        beforeEnter: requireWebSession
      },
      {
        path: '/' + ERouterName.MEMBERS,
        name: ERouterName.MEMBERS,
        component: () => import('/@/pages/page-web/projects/members.vue')
      },
      {
        path: '/' + ERouterName.ORGANIZATION,
        name: ERouterName.ORGANIZATION,
        component: () => import('/@/pages/page-web/projects/organization.vue')
      },
      {
        path: '/' + ERouterName.PROFILE,
        name: ERouterName.PROFILE,
        component: () => import('/@/pages/page-web/projects/profile.vue')
      },
      {
        path: '/' + ERouterName.DEVICES,
        name: ERouterName.DEVICES,
        component: () => import('/@/pages/page-web/projects/devices.vue')
      },
      {
        path: '/' + ERouterName.FIRMWARES,
        name: ERouterName.FIRMWARES,
        component: () => import('../pages/page-web/projects/Firmwares.vue')
      },
      {
        path: '/' + ERouterName.METADATA,
        name: ERouterName.METADATA,
        component: () => import('/@/pages/page-web/projects/metadata.vue')
      },
      {
        path: '/' + ERouterName.SYSTEM_SETTINGS,
        name: ERouterName.SYSTEM_SETTINGS,
        component: () => import('/@/pages/page-web/projects/system-settings.vue')
      },
      {
        path: '/' + ERouterName.MQTT,
        name: ERouterName.MQTT,
        component: () => import('/@/pages/page-web/projects/mqtt.vue'),
        beforeEnter: requireWebSession
      },
      {
        path: '/' + ERouterName.OPENAPI,
        name: ERouterName.OPENAPI,
        component: () => import('/@/pages/page-web/openapi/index.vue'),
        redirect: '/openapi/apps',
        children: [
          {
            path: '/openapi/apps',
            name: ERouterName.OPENAPI_APPS,
            component: () => import('/@/pages/page-web/openapi/apps.vue')
          },
          {
            path: '/openapi/docs',
            name: ERouterName.OPENAPI_DOCS,
            component: () => import('/@/pages/page-web/openapi/docs.vue')
          },
          {
            path: '/openapi/logs',
            name: ERouterName.OPENAPI_LOGS,
            component: () => import('/@/pages/page-web/openapi/logs.vue')
          }
        ]
      }
    ]
  },
  // workspace
  {
    path: '/' + ERouterName.WORKSPACE,
    name: ERouterName.WORKSPACE,
    component: () => import('/@/pages/page-web/projects/workspace.vue'),
    redirect: '/' + ERouterName.TSA,
    children: [
      {
        path: '/' + ERouterName.TSA,
        component: () => import('/@/pages/page-web/projects/tsa.vue')
      },
      {
        path: '/' + ERouterName.LAYER,
        name: ERouterName.LAYER,
        component: () => import('/@/pages/page-web/projects/layer.vue')
      },
      {
        path: '/' + ERouterName.MAPPING,
        name: ERouterName.MAPPING,
        component: () => import('/@/pages/page-web/projects/mapping.vue'),
        beforeEnter: requireWebSession
      },
      {
        path: '/' + ERouterName.MEDIA,
        name: ERouterName.MEDIA,
        component: () => import('/@/pages/page-web/projects/media.vue')
      },
      {
        path: '/' + ERouterName.WAYLINE,
        name: ERouterName.WAYLINE,
        component: () => import('/@/pages/page-web/projects/wayline.vue')
      },
      {
        path: '/' + ERouterName.TASK,
        name: ERouterName.TASK,
        component: () => import('/@/pages/page-web/projects/task.vue'),
        children: [
          {
            path: ERouterName.CREATE_PLAN,
            name: ERouterName.CREATE_PLAN,
            component: CreatePlan,
            children: [
              {
                path: ERouterName.SELECT_PLAN,
                name: ERouterName.SELECT_PLAN,
                components: {
                  WaylinePanel,
                  DockPanel
                }
              }
            ]
          }

        ]
      },
      {
        path: '/flight-record/:recordId',
        name: ERouterName.FLIGHT_RECORD_DETAIL,
        component: () => import('/@/pages/page-web/projects/flight-record-detail.vue')
      },
      {
        path: '/' + ERouterName.FLIGHT_AREA,
        name: ERouterName.FLIGHT_AREA,
        component: () => import('/@/pages/page-web/projects/flight-area.vue')
      },
    ]
  },
  // pilot
  {
    path: '/' + ERouterName.PILOT,
    name: ERouterName.PILOT,
    component: () => import('/@/pages/page-pilot/pilot-index.vue'),
  },
  {
    path: '/' + ERouterName.PILOT_HOME,
    component: () => import('/@/pages/page-pilot/pilot-home.vue')
  },
  {
    path: '/' + ERouterName.PILOT_MEDIA,
    component: () => import('/@/pages/page-pilot/pilot-media.vue')
  },
  {
    path: '/' + ERouterName.PILOT_LIVESHARE,
    component: () => import('/@/pages/page-pilot/pilot-liveshare.vue')
  },
  {
    path: '/' + ERouterName.PILOT_BIND,
    component: () => import('/@/pages/page-pilot/pilot-bind.vue')
  },
  {
    path: '/' + ERouterName.COCKPIT,
    name: ERouterName.COCKPIT,
    component: () => import('/@/pages/page-web/cockpit.vue')
  },
  {
    path: '/inspection',
    name: ERouterName.INSPECTION,
    component: () => import('/@/pages/page-web/inspection/index.vue'),
    redirect: '/inspection/control',
    beforeEnter: requireWebSession,
    children: [
      {
        path: 'control',
        name: ERouterName.INSPECTION_CONTROL,
        component: () => import('/@/pages/page-web/inspection/control.vue')
      },
      {
        path: 'video-wall',
        name: ERouterName.INSPECTION_VIDEO_WALL,
        component: () => import('/@/pages/page-web/inspection/video-wall.vue')
      },
      {
        path: 'live-detection',
        name: ERouterName.INSPECTION_LIVE_DETECTION,
        component: () => import('/@/pages/page-web/inspection/live-detection.vue')
      },
      {
        path: 'events',
        name: ERouterName.INSPECTION_EVENTS,
        component: () => import('/@/pages/page-web/inspection/events.vue')
      },
      {
        path: 'data',
        name: ERouterName.INSPECTION_DATA,
        component: () => import('/@/pages/page-web/inspection/data.vue')
      },
      {
        path: 'flight-management',
        name: ERouterName.INSPECTION_FLIGHT_MANAGEMENT,
        component: () => import('/@/pages/page-web/inspection/flight-management.vue')
      },
      {
        path: 'analytics',
        name: ERouterName.INSPECTION_ANALYTICS,
        component: () => import('/@/pages/page-web/inspection/analytics.vue')
      },
      {
        path: 'phase-two',
        name: ERouterName.INSPECTION_PHASE_TWO,
        component: () => import('/@/pages/page-web/inspection/phase-two.vue')
      },
      {
        path: 'settings',
        name: ERouterName.INSPECTION_SETTINGS,
        component: () => import('/@/pages/page-web/inspection/settings.vue')
      },
      {
        path: 'scenic-bindings',
        name: ERouterName.INSPECTION_SCENIC_BINDINGS,
        component: () => import('/@/pages/page-web/inspection/scenic-bindings.vue')
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

export default router
